/* eslint-disable @typescript-eslint/dot-notation */
import { Component, Injectable, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter } from '@angular/material/core';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import moment from 'moment';

import { DateFormatEnum } from '../../models/date-format.enum';
import { DateInputEnum } from '../../models/date-input.enum';
import { DetectUserAgent } from '../../utils/detect-user-agent';


@Injectable()
export class seUiDateAdapter extends MomentDateAdapter {
    getFirstDayOfWeek = () => 1
}
/**
 * Editor Custom AgGrid for Date DateTime Time Input.
 * Params:
 * - inputType: (Optional) it allows to specify 'DATE_TIME', 'DATE' or 'TIME'. Default value DATE.
 * - inputFormat: (Optional) it allows to specify 'SHORT' 'LONG' format for DATE_TIME, DATE e TIME. Default value SHORT.
 * - required: (Optional) It allows to specify required validator
 * - inlineEditor (Default true) it allows to specify the preference between online or popup editor version.
 *  NOTE: when in the AgGrid is enabled the fullRow editing this input param is ignored and the editor will be inline
 */
@Component({
    selector: 'se-date-cell-editor',
    templateUrl: './date-cell-editor.component.html',
    styleUrls: ['./date-cell-editor.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        { provide: DateAdapter, useClass: seUiDateAdapter }
    ]

})
export class DateCellEditorComponent implements ICellEditorAngularComp {
    public inputType: DateInputEnum;
    public inputFormat: DateFormatEnum;
    public editInline: boolean;
    public required: boolean

    value: moment.Moment;
    originalValue: moment.Moment;
    params: ICellEditorParams;
    cellWidth: string;
    cellStartEdited: boolean;
    formControl: FormControl;
    inlineEditor: boolean;
    dateSeparator: string;
    timeSeparator: string;
    dateInputFormat: string;
    timeInputFormat: string;
    dateTimeInputFormat: string;


    constructor(private detectUserAgent: DetectUserAgent) {

    }

    agInit(params: ICellEditorParams): void {
        this.params = params;
        this.cellWidth = params.column.getActualWidth() + 'px';

        this.inlineEditor = (this.params.api.getEditingCells().length) > 1;
        // Recupero parametri input
        this.configCellEditor();

        this.convertValueToMoment();

        this.cellStartEdited = this.params.cellStartedEdit;

    }

    getValue(): any {
        if (this.formControl && !this.formControl.invalid) {

            const formVal = this.formControl.value;

            if (moment.isMoment(formVal)) {
                this.value = formVal;
            } else {
                this.value = moment(formVal);
            }

            if (this.originalValue && this.originalValue.isSame(this.value)) {
                return this.params.value;
            }
            // Alla chiusura dell'editor se il campo è valido aggiorniamo il valore altrimenti no
            this.updateOriginalValue();

            return this.originalValue ? this.originalValue.toDate() : null;

        }
        return undefined;
    }

    isPopup(): boolean {
        // lasciamo sempre TRUE anche quando instanziamo il componente inline per le DATE
        // altrimenti non riusciamo a selezionare i dati dal picker
        return true;
    }

    _onFormReady(editorFromControl: FormControl) {
        this.formControl = editorFromControl;
    }

    _onSpaceBarClick(event: KeyboardEvent) {
        event.preventDefault();
        this.formControl.setValue(moment(Date.now()));
    }

    _onEscPressed(event: KeyboardEvent) {
        this.formControl.setValue(this.originalValue)
        this._onEnterPressed(event);
    }

    _onEnterPressed(event: KeyboardEvent) {
        event.preventDefault();
        this.params.api.stopEditing();
    }

    _tabToTheNextCell() {
        this.params.api.tabToNextCell();
    }

    private configCellEditor() {
        const inputTypeParam = this.params['inputType'];
        this.inputType = inputTypeParam ? inputTypeParam : DateInputEnum.DATE;

        const inputFormatParam = this.params['inputFormat'];
        this.inputFormat = inputFormatParam ? inputFormatParam : DateFormatEnum.SHORT;

        this.editInline = this.params['inlineEditor'];
        this.editInline = (this.editInline !== null && this.editInline !== undefined) ? this.editInline : true;

        this.inlineEditor = (this.params.api.getEditingCells().length) === 1 ? this.editInline : true;
        this.required = this.params['required'];
        this.dateInputFormat = this.params['dateInputFormat'] || 'DDMMYYYY';
        this.timeInputFormat = this.params['timeInputFormat'] || 'HH:mm';
        this.dateTimeInputFormat = this.params['dateTimeInputFormat'] || 'DDMMYYYY';
        this.dateSeparator = this.params['dateSeparator'] || '/';
        this.timeSeparator = this.params['timeSeparator'] || ':';
    }


    private updateOriginalValue() {
         if (!this.value.isValid()) {
             this.originalValue = null;
             return;
        }
        switch (this.inputType) {
            case DateInputEnum.DATE_TIME:
                if (this.originalValue) {
                    this.originalValue.set({
                        date: this.value.date(),
                        month: this.value.month(),
                        year: this.value.year(),
                        hour: this.value.hour(),
                        minute: this.value.minute(),
                        second: this.value.second(),
                        millisecond: this.value.millisecond()
                    });
                } else {
                    this.originalValue = moment();
                    this.originalValue.set({
                        date: this.value.date(),
                        month: this.value.month(),
                        year: this.value.year(),
                        hour: this.value.hour(),
                        minute: this.value.minute(),
                        second: this.value.second(),
                        millisecond: this.value.millisecond()
                    });
                }
                break;
            case DateInputEnum.DATE:
                // prendiamo la data aggioranta da this.value ma il time lasciamo quello originale se presente altrimenti 0:00:00:00
                if (!this.originalValue) {
                    this.originalValue = moment();
                    this.originalValue.set({
                        date: this.value.date(),
                        month: this.value.month(),
                        year: this.value.year(),
                        hour: 0,
                        minute: 0,
                        second: 0,
                        millisecond: 0
                    });
                } else {
                    this.originalValue.set({
                        date: this.value.date(),
                        month: this.value.month(),
                        year: this.value.year(),
                    });
                }
                break;
            case DateInputEnum.TIME:
                // prendiamo il time da this.value ma la data lasciamo quella originale
                // se date null indichiamo il  January 1, 1970
                if (!this.originalValue) {
                    this.originalValue = moment("01-01-1970", "MM-DD-YYYY");
                }

                this.originalValue.set({
                    hour: this.value.hour(),
                    minute: this.value.minute(),
                    second: this.value.second(),
                    millisecond: this.value.millisecond()
                });

                break;
        }
    }

    private convertValueToMoment() {
        if (this.params.value === null) {
            this.originalValue = null;
            this.value = moment('').utc(); // DA VERIFICARE
            return;
        } else {
            this.originalValue = moment(this.params.value);
            this.value = moment(this.params.value).utc();
        }
    }

    private insertSeparatorsIntoString(input: string, separator: string): string {
        let splitted = input.split(''),
            i = 0;
        while (i < splitted.length - 1) {
            if (splitted[i] !== splitted[i + 1]) {
                splitted = [...splitted.slice(0, i + 1), separator, ...splitted.slice(i + 1)];
                i++;
            }
            i++;
        }
        return splitted.join("");
    }

    private dateFormatToConfiguration(dateInputFormat: string): string {
        let output: string;
        output = dateInputFormat ? dateInputFormat.replace(new RegExp('[^dmy]', 'ig'), '') : 'DDMMYYYY';
        return output.toUpperCase();
    }

}
